/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';

// API endpoint name - should match configuration in AWS Amplify
const API_NAME = 'resourceManagementADI';

/**
 * TypeScript interfaces for image management
 */
export interface ImageThumbnail {
	url: string;
	dimensions?: {
		width: number;
		height: number;
	};
	canEdit?: boolean;
	canDelete?: boolean;
}

export interface ImageMetadata {
	camera?: string;
	lens?: string;
	exposure?: string;
	aperture?: string;
	iso?: string;
	focalLength?: string;
	dateTaken?: string;
	location?: {
		latitude?: number;
		longitude?: number;
		placeName?: string;
	};
}

export interface ImageViewerSettings {
	enableDownload?: boolean;
	enableShare?: boolean;
	enableCopy?: boolean;
	showMetadata?: boolean;
}

export interface ImageFile {
	title: string;
	description?: string;
	url?: string;
	thumbnail?: ImageThumbnail;
	fileType?: string;
	fileSize?: number;
	dimensions: {
		width: number;
		height: number;
	};
	order: number;
	uploadedAt: string;
	categoryIds: string[];
	tags: string[];
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility?: 'private' | 'public';
	allowDownload: boolean;
	allowShare: boolean;
	accessControl?: {
		internalEmployees?: {
			allSelected?: boolean;
			departments?: Array<{name: string; count?: number}>;
			specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
		};
		userGroups?: Array<{type: string; count?: number}>;
		participantCount?: number;
		participantLimit?: number;
	};
}

export interface CreateImagePayload {
	companyId: string;
	sourceType: 'system' | 'user' | 'external' | 'media_drive' | 'embed_link';
	files: ImageFile[];
	publishAt?: string;
	sourceId?: string;
	viewerSettings?: ImageViewerSettings;
}

export interface ImageCreatedBy {
	userId: string;
	name: string;
}

export interface ImageEngagement {
	views: number;
	downloads: number;
	helpful: number;
	notHelpful: number;
}

export interface ImageResponse {
	_id: string;
	companyId: string;
	sourceType: 'system' | 'user' | 'external' | 'media_drive';
	files: ImageFile[];
	createdBy: ImageCreatedBy;
	updatedBy: ImageCreatedBy;
	engagement: ImageEngagement;
	createdAt: string;
	updatedAt: string;
	publishedAt?: string;
	scheduledPublishDate?: string;
	viewerSettings?: ImageViewerSettings;
	__v: number;
}

export interface ImageListResponse {
	data: ImageResponse[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

export interface UpdateImagePayload {
	sourceType?: 'system' | 'user' | 'external' | 'media_drive';
	files?: Array<{
		title?: string;
		description?: string;
		url?: string;
		fileType?: string;
		fileSize?: number;
		dimensions?: {
			width: number;
			height: number;
		};
		order?: number;
		uploadedAt?: string;
		categoryIds?: string[];
		tags?: string[];
		status?: 'draft' | 'published' | 'scheduled' | 'unpublished';
		visibility?: 'private' | 'public';
		allowDownload?: boolean;
		allowShare?: boolean;
		accessControl?: {
			internalEmployees?: {
				allSelected?: boolean;
				departments?: Array<{name: string; count?: number}>;
				specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
			};
			userGroups?: Array<{type: string; count?: number}>;
			participantCount?: number;
			participantLimit?: number;
		};
	}>;
}

/**
 * Create a new image collection
 * @param imagePayload - Image collection payload
 * @returns Promise with API response
 */
export const createImageCollection = async (imagePayload: CreateImagePayload) =>
{
	try
	{
		const apiResponse = await API.post(API_NAME, '/images', {
			body : imagePayload
		});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error creating image collection:', error);
		throw error;
	}
};

/**
 * Get all image collections for a company
 * @param companyId - Company ID
 * @param options - Additional options like pagination, filters
 * @returns Promise with API response
 */
export const getImageCollections = async (
	companyId: string,
	options: {
		page?: number;
		limit?: number;
		status?: string;
		category?: string;
		search?: string;
	} = {},
	from = 'Configuration'
) =>
{
	try
	{
		const { page, limit, status, category, search } = options;

		let queryParams = '';
		const path = `/images/company/${companyId}`;

		if (page) queryParams += `&page=${page}`;
		if (limit) queryParams += `&limit=${limit}`;
		if (status) queryParams += `&status=${status}`;
		if (category) queryParams += `&category=${category}`;
		if (search) queryParams += `&search=${encodeURIComponent(search)}`;
		if (from) queryParams += `&from=${from}`;

		const apiResponse = await API.get(API_NAME, `${path}?${queryParams}`, {});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error fetching image collections:', error);
		throw error;
	}
};

/**
 * Get a single image collection by ID
 * @param collectionId - Image collection ID
 * @returns Promise with API response
 */
export const getImageCollectionById = async (collectionId: string) =>
{
	try
	{
		const apiResponse = await API.get(API_NAME, `/images/${collectionId}`, {});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error fetching image collection:', error);
		throw error;
	}
};

/**
 * Delete an image collection
 * @param collectionId - Image collection ID
 * @returns Promise with API response
 */
export const deleteImageCollection = async (collectionId: string) =>
{
	try
	{
		const apiResponse = await API.del(API_NAME, `/images/${collectionId}`, {});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error deleting image collection:', error);
		throw error;
	}
};

/**
 * Get list of images for a company
 * @param companyId - Company ID
 * @param params - Query parameters (pagination, filters, etc.)
 * @returns Promise with list of images
 */
export const getImagesList = async (
	companyId: string,
	params?: {
		page?: number;
		limit?: number;
		status?: string;
		category?: string;
		subCategory?: string;
		tag?: string;
		sourceType?: string;
		sort?: string;
	}
): Promise<ImageListResponse> =>
{
	try
	{
		let path = `/images/company/${companyId}`;
		const queryParams = new URLSearchParams();

		if (params?.page) queryParams.append('page', params.page.toString());
		if (params?.limit) queryParams.append('limit', params.limit.toString());
		if (params?.status) queryParams.append('status', params.status);
		if (params?.category) queryParams.append('category', params.category);
		if (params?.subCategory) queryParams.append('subCategory', params.subCategory);
		if (params?.tag) queryParams.append('tag', params.tag);
		if (params?.sourceType) queryParams.append('sourceType', params.sourceType);
		if (params?.sort) queryParams.append('sort', params.sort);

		const queryString = queryParams.toString();

		if (queryString)
		{
			path += `?${queryString}`;
		}

		const response = await API.get(API_NAME, path, {});

		return response;
	}
	catch (error)
	{
		console.error('Error fetching images:', error);
		throw error;
	}
};

/**
 * Get a single image collection by ID
 * @param imageId - Image ID
 * @returns Promise with image data
 */
export const getImageById = async (imageId: string, queryStringParameters): Promise<ImageResponse> =>
{
	try
	{
		const response = await API.get(API_NAME, `/images/${imageId}`, { queryStringParameters });

		return response.data;
	}
	catch (error)
	{
		console.error('Error fetching image:', error);
		throw error;
	}
};

/**
 * Update an existing image collection
 * @param imageId - Image ID to update
 * @param payload - Updated image data
 * @returns Promise with updated image data
 */
export const updateImage = async (
	imageId: string,
	payload: Partial<CreateImagePayload>
): Promise<ImageResponse> =>
{
	try
	{
		const response = await API.put(API_NAME, `/images/${imageId}`, {
			body : payload
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating image:', error);
		throw error;
	}
};

/**
 * Delete an image collection
 * @param imageId - Image ID to delete
 * @returns Promise with deletion confirmation
 */
export const deleteImage = async (imageId: string): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.del(API_NAME, `/images/${imageId}`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error deleting image:', error);
		throw error;
	}
};

/**
 * Add a file to an existing image collection
 * @param imageId - Image collection ID
 * @param fileData - File data to add
 * @returns Promise with updated image collection
 */
export const addImageFile = async (
	imageId: string,
	fileData: ImageFile
): Promise<ImageResponse> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/${imageId}/file`, {
			body : fileData
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error adding image file:', error);
		throw error;
	}
};

/**
 * Remove a file from an image collection
 * @param imageId - Image collection ID
 * @param fileIndex - Index of the file to remove
 * @returns Promise with updated image collection
 */
export const removeImageFile = async (
	imageId: string,
	fileIndex: number
): Promise<{ success: boolean; data: ImageResponse }> =>
{
	try
	{
		const response = await API.del(API_NAME, `/images/${imageId}/file/${fileIndex}`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error removing image file:', error);
		throw error;
	}
};

/**
 * Update status for all files or a specific file in an image collection
 * @param imageId - Image collection ID
 * @param status - New status
 * @param fileIndex - Optional index of specific file to update
 * @param scheduledPublishDate - Date for scheduled publishing (required if status is "scheduled")
 * @returns Promise with updated image collection
 */
export const updateImageStatus = async (
	imageId: string,
	status: 'draft' | 'published' | 'scheduled' | 'unpublished',
	fileIndex?: number,
	scheduledPublishDate?: number
): Promise<{ success: boolean; data: ImageResponse; message: string }> =>
{
	try
	{
		const payload: {
			status: string;
			fileIndex?: number;
			scheduledPublishDate?: number
		} = { status };

		if (fileIndex !== undefined)
		{
			payload.fileIndex = fileIndex;
		}

		if (status === 'scheduled' && scheduledPublishDate)
		{
			payload.scheduledPublishDate = scheduledPublishDate;
		}

		const response = await API.patch(API_NAME, `/images/${imageId}/status`, {
			body : { ...payload, fileIndex: 0 }
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating image status:', error);
		throw error;
	}
};

/**
 * Track image view
 * @param imageId - Image ID
 * @returns Promise with confirmation
 */
export const trackImageView = async (imageId: string): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/${imageId}/view`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error tracking image view:', error);
		throw error;
	}
};

/**
 * Track image download
 * @param imageId - Image ID
 * @returns Promise with confirmation
 */
export const trackImageDownload = async (imageId: string): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/${imageId}/download`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error tracking image download:', error);
		throw error;
	}
};

/**
 * Update engagement metrics
 * @param imageId - Image ID
 * @param metric - Metric to update
 * @param value - Value to add
 * @returns Promise with updated engagement metrics
 */
export const updateImageEngagement = async (
	imageId: string,
	metric: 'helpful' | 'notHelpful' | 'downloads' | 'views',
	value: number
): Promise<{ success: boolean; data: { engagement: any }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/${imageId}/engage`, {
			body : { metric, value }
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating image engagement:', error);
		throw error;
	}
};

/**
 * Get image color statistics
 * @param imageUrl - URL of the image
 * @returns Promise with color data
 */
export const getImageColorStats = async (
	imageUrl: string
): Promise<{ dominant: string; palette: string[] }> =>
{
	try
	{
		const response = await API.get(API_NAME, '/images/analyze-color', {
			queryStringParameters : { url: imageUrl }
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error analyzing image colors:', error);
		throw error;
	}
};

/**
 * Get image metadata
 * @param imageUrl - URL of the image
 * @returns Promise with metadata
 */
export const getImageMetadata = async (
	imageUrl: string
): Promise<ImageMetadata> =>
{
	try
	{
		const response = await API.get(API_NAME, '/images/metadata', {
			queryStringParameters : { url: imageUrl }
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error fetching image metadata:', error);
		throw error;
	}
};

/**
 * Track image engagement (view/download/etc.)
 * @param imageId - Collection ID
 * @param fileIndex - Index of the file
 * @param engagementType - Type of engagement
 * @returns Promise with success status
 */
export const trackImageEngagement = async (
	imageId: string,
	fileIndex: number,
	engagementType: 'view' | 'download' | 'share' | 'like'
): Promise<{ success: boolean }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/${imageId}/engagement`, {
			body : {
				fileIndex,
				type : engagementType
			}
		});

		return response;
	}
	catch (error)
	{
		console.error('Error tracking image engagement:', error);
		throw error;
	}
};

/**
 * Toggle favorite status for an image
 * @param companyId - Company ID
 * @param imageId - Image ID
 * @returns Promise with favorite status and updated image
 */
export const toggleImageFavorite = async (
	companyId: string,
	imageId: string
): Promise<{ success: boolean; data: { image: ImageResponse; isFavorited: boolean; favoriteCount: number }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/images/company/${companyId}/${imageId}/favorite`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error toggling image favorite:', error);
		throw error;
	}
};